import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MessageService } from 'primeng/api';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../environments/environment';

import { Select, Store } from '@ngxs/store';
import {GetLanguages, LoginSuccess, NewAlert} from './core/store/shared/shared.actions';
import { SharedState } from './core/store/shared/shared.state';
import { GetUsersConfiguration } from './core/store/users/users.actions';
import { UsersSelectors } from './core/store/users/users.selectors';

import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { ToastNotification } from './core/models/notification-payload';
import { SidebarSize } from './core/models/sidebar-size';
import { UserConfigurationMetaData } from './core/models/user/users-query-result';
import { LoginService } from './core/services/login-service';
import { EnvService } from './models/env.service';
import { CommonService } from './services/common.service';
import { UserService } from './core/services/user.service';
import { ServerConnectionStatus } from './core/types/ServerConnectionStatus.type';
import { ServerDisconnectedErrorComponent } from './shared/components/snack-bar/server-disconnected-error/server-disconnected-error.component';
import { NetworkConnectionErrorComponent } from './shared/components/snack-bar/network-connection-error/network-connection-error.component';
import {GetConfigurations} from './core/store/lockdown/lockdown.actions';
import {GetEventsConfiguration} from './core/store/events/events.actions';
import {GetNetworksConfiguration} from './core/store/networks/networks.actions';
import {GetReportsConfiguration} from './core/store/reports/reports.actions';
import {TranslateService} from '@ngx-translate/core';
import { LanguageSettings } from './core/store/shared/shared.model';
import {MonitoringService} from './services/logging.service';
import {EventDismissComponent} from './modules/events/components/event-dismiss/event-dismiss.component';
import {MatDialog} from '@angular/material/dialog';
import {GetDoorsConfiguration} from './core/store/access-point/access-point.actions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [MessageService]
})
export class AppComponent implements OnInit, OnDestroy {

  public sidebarSize = SidebarSize;
  public env: any = environment;
  public appVersion = this.envProvider.environment.version;
  public versionNumber = this.envProvider.environment.versionNumber;
  public userPermissions = null;
  private destroy$ = new Subject<boolean>();
  public isToastOpened: boolean = false;
  public languageWasSet: boolean = false;
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;

  @Select(SharedState.isDarkTheme) isDarkTheme$: Observable<boolean>;
  @Select(SharedState.sidebarSize) sidebarSize$: Observable<SidebarSize>;
  @Select(SharedState.toastMessage) toastMessage$: Observable<ToastNotification>;
  @Select(SharedState.serverConnectionStatus) serverConnectionStatus$: Observable<ServerConnectionStatus>;
  @Select(UsersSelectors.configuration) usersConfiguration$: Observable<UserConfigurationMetaData>;
  @Select(SharedState.languageSettings) languageSettings$: Observable<LanguageSettings>;
  @Select(SharedState.newAlert) newAlert$: Observable<NewAlert>;

  @HostListener('window:beforeunload')
  navigateToLoginScreen() {
    if (localStorage.getItem('id_token')) {
      return this.loginService.logoutUser();
    }
  }

  constructor(
    private readonly store: Store,
    private readonly loginService: LoginService,
    private messageService: MessageService,
    private titleService: Title,
    private envProvider: EnvService,
    public router: Router,
    private commonService: CommonService,
    private oauthService: OAuthService,
    private readonly userService: UserService,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    public monitoringService: MonitoringService,
    public dialog: MatDialog,
  ) {
  }

  public get isLoggedIn$() {
    return this.loginService.isLoggedIn$;
  }

  //TODO make the isLogged in decorated by @Select

  ngOnInit() {

    this.newAlert$
      .subscribe((eventItem)=>{
        if(eventItem) {
          this.dialog.open(EventDismissComponent, {
            data: {eventItem},
            backdropClass: 'alarm-backdrop',
            panelClass: 'alarm-popup'
          });
        }
      });

    this.titleService.setTitle(`${this.envProvider.environment.appTitle}`);
    this.serverConnectionStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status === 'Connected') return;

        if(this.envProvider.environment.showNetworkConnectionMessages){
          this.snackBar.openFromComponent(
            status === 'BackendDisconnected' ? ServerDisconnectedErrorComponent : NetworkConnectionErrorComponent,
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
            }
          );
        }
      });

    this.isLoggedIn$
      .pipe(
        filter(data => !!data),
        takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(new GetLanguages());
        this.store.dispatch(new LoginSuccess('1'));
        this.store.dispatch(new GetUsersConfiguration());
        this.store.dispatch(new GetDoorsConfiguration());
        this.store.dispatch(new GetConfigurations());
        this.store.dispatch(new GetEventsConfiguration());
        this.store.dispatch(new GetNetworksConfiguration());
        this.store.dispatch(new GetReportsConfiguration());
      });

    this.usersConfiguration$
      .pipe(
        filter(conf => !!conf),
        takeUntil(this.destroy$))
      .subscribe(conf => {
        if (conf.userTypes) {
          this.commonService.userTypeMapper = [...conf.userTypes];
        }
      });

    this.toastMessage$
      .pipe(
        filter(data => !!data),
        takeUntil(this.destroy$))
      .subscribe(payload => {
        this.messageService.add({severity: payload.severity, summary: payload.summary, detail: payload.detail});
        // setTimeout(() => {
        //   this.isToastOpened = false;
        // }, 4000);
      });

    this.languageSettings$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (settings) => {
        if (!settings) {
          return;
        }
        if (!settings.supportedLanguages
          || settings.supportedLanguages.length === 0
          || !settings.systemLanguage) {
          this.translate.addLangs(['en']);
          this.translate.setDefaultLang('en');
          this.languageWasSet = true;
        } else {
          this.translate.addLangs(settings.supportedLanguages.map(lang => lang.code2));
          let existedLanguage = settings.supportedLanguages.find(lang => lang.code2 === settings.systemLanguage.code2);
          if (!existedLanguage) {
            this.translate.setDefaultLang('en');
            this.languageWasSet = true;
            return;
          }
          let isFileExistResult = await this.commonService.isLanguageFileExist(settings.systemLanguage.code2);
          if (!isFileExistResult) {
            this.translate.setDefaultLang('en');
            this.languageWasSet = true;
            return;
          }
          this.translate.setDefaultLang(settings.systemLanguage.code2);
          this.languageWasSet = true;
        }
      });

  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
